// src/components/BackupMGMT.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { formatNY } from '../utils/dateRange';

const CRON_PRESETS = [
  { label: 'Daily at 2:00 AM', value: '0 2 * * *' },
  { label: 'Daily at midnight', value: '0 0 * * *' },
  { label: 'Daily at 3:00 AM', value: '0 3 * * *' },
  { label: 'Every 12 hours', value: '0 0,12 * * *' },
  { label: 'Weekly (Sunday 2 AM)', value: '0 2 * * 0' },
];

const formatBytes = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDuration = (ms) => {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const StatusBadge = ({ status }) => {
  const styles = {
    success: 'bg-green-100 text-green-800',
    failed:  'bg-red-100 text-red-800',
    running: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status === 'running' && (
        <svg className="animate-spin -ml-0.5 mr-1 h-3 w-3 text-yellow-600" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
      )}
      {status ? status.charAt(0).toUpperCase() + status.slice(1) : '—'}
    </span>
  );
};

function BackupMGMT() {
  const [config, setConfig]           = useState(null);
  const [configExpanded, setConfigExpanded] = useState(false);
  const [history, setHistory]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [running, setRunning]         = useState(false);
  const [testing, setTesting]         = useState(false);
  const [testResult, setTestResult]   = useState(null);
  const [toast, setToast]             = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const pollRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get('/backup/history');
      setHistory(data.jobs || []);
    } catch { /* ignore */ }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await axios.get('/backup/config');
      const cfg = data.config || {};
      setConfig(cfg);
      // Expand if no remote is set yet (first-time setup); collapse if already configured
      setConfigExpanded(!cfg.rclone_remote);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchConfig(), fetchHistory()]).finally(() => setLoading(false));
    return () => clearInterval(pollRef.current);
  }, [fetchConfig, fetchHistory]);

  const pollJob = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`/backup/job/${jobId}`);
        if (data.job.status !== 'running') {
          clearInterval(pollRef.current);
          setRunning(false);
          fetchHistory();
          if (data.job.status === 'success') showToast('Backup completed successfully');
          else showToast(`Backup failed: ${data.job.error_message || 'unknown error'}`);
        }
      } catch {
        clearInterval(pollRef.current);
        setRunning(false);
      }
    }, 3000);
  };

  const handleRunNow = async () => {
    setRunning(true);
    clearInterval(pollRef.current);
    try {
      const { data } = await axios.post('/backup/run');
      showToast('Backup started…');
      await fetchHistory();
      pollJob(data.jobId);
    } catch (err) {
      setRunning(false);
      showToast(err.response?.data?.error || 'Failed to start backup');
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('/backup/config', config);
      setConfig(data.config);
      if (data.config?.rclone_remote) setConfigExpanded(false);
      showToast('Configuration saved');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save config');
    } finally {
      setSaving(false);
    }
  };

  const handleTestRclone = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await axios.post('/backup/test-rclone', { remote: config?.rclone_remote });
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  const set = (field, value) => setConfig((prev) => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh]">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Backup MGMT</h1>
        <button
          onClick={handleRunNow}
          disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 min-h-[44px]"
        >
          {running ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              Running…
            </>
          ) : (
            <>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              Run Backup Now
            </>
          )}
        </button>
      </div>

      {/* Config Card */}
      <div className="bg-white rounded-lg shadow mb-6">
        <button
          onClick={() => setConfigExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-4 text-left min-h-[56px]"
        >
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-base font-semibold text-gray-900 shrink-0">Configuration</h2>
            {!configExpanded && config?.rclone_remote && (
              <div className="flex items-center gap-2 text-sm text-gray-500 truncate">
                <span className="font-mono text-gray-700">{config.rclone_remote}</span>
                <span>→</span>
                <span>{config.rclone_path || 'affiliate-backups'}</span>
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline">{CRON_PRESETS.find((p) => p.value === config.cron_schedule)?.label || config.cron_schedule}</span>
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium shrink-0 ${config.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {config.enabled ? 'On' : 'Off'}
                </span>
              </div>
            )}
            {!configExpanded && !config?.rclone_remote && (
              <span className="text-sm text-amber-600">Not configured</span>
            )}
          </div>
          <svg className={`w-4 h-4 text-gray-400 shrink-0 ml-2 transition-transform ${configExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        {configExpanded && <div className="border-t border-gray-200"/>}
        {configExpanded && <div className="p-4 space-y-4">
          {/* Enabled toggle */}
          <div className="flex items-center justify-between min-h-[44px]">
            <div>
              <div className="text-sm font-medium text-gray-900">Enable scheduled backups</div>
              <div className="text-xs text-gray-500">Runs automatically per the schedule below</div>
            </div>
            <button
              onClick={() => set('enabled', !config?.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${config?.enabled ? 'bg-blue-600' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${config?.enabled ? 'translate-x-6' : 'translate-x-1'}`}/>
            </button>
          </div>

          {/* Schedule */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
            <select
              value={config?.cron_schedule || '0 2 * * *'}
              onChange={(e) => set('cron_schedule', e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">All times in America/New_York</p>
          </div>

          {/* rclone remote */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">rclone Remote Name</label>
            <input
              type="text"
              value={config?.rclone_remote || ''}
              onChange={(e) => set('rclone_remote', e.target.value)}
              placeholder="e.g. gdrive, onedrive, dropbox"
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              The remote name you configured with <code className="bg-gray-100 px-1 rounded">rclone config</code> on the server.
              Leave blank to skip cloud upload.
            </p>
          </div>

          {/* Remote path */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remote Path</label>
            <input
              type="text"
              value={config?.rclone_path || 'affiliate-backups'}
              onChange={(e) => set('rclone_path', e.target.value)}
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Retention */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Retention (days)</label>
            <input
              type="number"
              inputMode="decimal"
              value={config?.retention_days ?? 14}
              onChange={(e) => set('retention_days', parseInt(e.target.value) || 14)}
              min={1}
              max={365}
              className="w-full sm:w-32 border border-gray-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="mt-1 text-xs text-gray-500">Old remote dumps older than this are deleted automatically.</p>
          </div>

          {/* Test + Save */}
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              onClick={handleTestRclone}
              disabled={testing || !config?.rclone_remote}
              className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
            >
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 min-h-[44px]"
            >
              {saving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>

          {testResult && (
            <div className={`flex items-start gap-2 p-3 rounded-md text-sm ${testResult.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              <span className="font-medium">{testResult.ok ? '✓ Connected successfully' : '✗ Connection failed'}</span>
              {testResult.error && <span className="ml-1 font-mono text-xs">{testResult.error}</span>}
            </div>
          )}
        </div>}
      </div>

      {/* Setup Instructions (collapsible) */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg mb-6">
        <button
          onClick={() => setShowInstructions(!showInstructions)}
          className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-blue-800 min-h-[44px]"
        >
          <span>How to set up rclone (Google Drive / OneDrive / Dropbox)</span>
          <svg className={`w-4 h-4 transition-transform ${showInstructions ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        {showInstructions && (
          <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
            <p>Run on the Ubuntu server (rclone is pre-installed in the backend container):</p>
            <pre className="bg-blue-100 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap">{`# SSH into the server, then:
rclone config

# Follow the prompts:
#   n) New remote → name it: gdrive / onedrive / dropbox
#   Choose type:  drive (Google), onedrive, dropbox
#   Complete OAuth flow in your browser

# Headless server tip — run this on your LOCAL PC first:
rclone authorize "drive"
# Copy the token, then paste it when prompted on the server.`}</pre>

            <p>After configuring, restart the backend container so it picks up the new config:</p>
            <pre className="bg-blue-100 rounded p-3 text-xs overflow-x-auto">{`docker-compose restart backend`}</pre>

            <p className="font-medium">Provider free storage: Google Drive 15 GB · OneDrive 5 GB (1 TB with M365) · Dropbox 2 GB</p>
          </div>
        )}
      </div>

      {/* Backup History */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Backup History</h2>
          <button onClick={fetchHistory} className="text-sm text-blue-600 hover:text-blue-800 min-h-[44px] flex items-center px-2">
            Refresh
          </button>
        </div>

        {history.length === 0 ? (
          <div className="px-4 py-10 text-center text-gray-500 text-sm">
            No backups yet. Click "Run Backup Now" to create the first one.
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {history.map((job) => (
                <div key={job.id} className="px-4 py-3 space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{formatNY(job.started_at)}</span>
                    <StatusBadge status={job.status} />
                  </div>
                  <div className="text-sm text-gray-800 font-medium truncate">{job.file_name || '—'}</div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>{formatBytes(job.file_size_bytes)}</span>
                    <span>{formatDuration(job.duration_ms)}</span>
                    <span className="truncate">{job.destination || '—'}</span>
                  </div>
                  {job.error_message && (
                    <div className="text-xs text-red-600 bg-red-50 rounded p-1.5 mt-1">{job.error_message}</div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {['Date (ET)', 'Status', 'File', 'Size', 'Duration', 'Destination'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {history.map((job) => (
                    <React.Fragment key={job.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatNY(job.started_at)}</td>
                        <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                        <td className="px-4 py-3 text-sm text-gray-800 font-mono max-w-[200px] truncate" title={job.file_name}>{job.file_name || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatBytes(job.file_size_bytes)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDuration(job.duration_ms)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate" title={job.destination}>{job.destination || '—'}</td>
                      </tr>
                      {job.error_message && (
                        <tr className="bg-red-50">
                          <td colSpan={6} className="px-4 py-2 text-xs text-red-700 font-mono">{job.error_message}</td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default BackupMGMT;
